import React from 'react';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  ExtractedResourceProvenance,
  isDocumentExtractionV2Resource,
} from './ExtractedResourceProvenance';

const useStyles = makeStyles(theme => ({
  callout: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.pxToRem(8),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
  },
  section: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    fontSize: theme.pxToRem(11),
    fontWeight: theme.typography.fontWeightBold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.hint,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: theme.spacing(4),
    rowGap: theme.spacing(2),
  },
  field: {
    minWidth: 0,
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  label: {
    display: 'block',
    marginBottom: theme.spacing(0.25),
    fontSize: theme.pxToRem(11),
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  value: {
    display: 'block',
    fontSize: theme.pxToRem(14),
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    overflowWrap: 'anywhere',
  },
}));

const isEmptyValue = (value: React.ReactNode): boolean =>
  value === null || value === undefined || value === '' || value === '—';

/**
 * A single label/value pair in a resource detail modal. Renders nothing when
 * the value is empty (null/undefined/blank/em-dash) so detail grids stay
 * compact instead of filling with placeholder dashes.
 */
export const DetailField: React.FC<{
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}> = ({ label, value, fullWidth }) => {
  const classes = useStyles();

  if (isEmptyValue(value)) {
    return null;
  }

  return (
    <div className={`${classes.field} ${fullWidth ? classes.fullWidth : ''}`}>
      <span className={classes.label}>{label}</span>
      <span className={classes.value}>{value}</span>
    </div>
  );
};

/**
 * A titled group of detail fields laid out in a compact two-column grid.
 */
export const DetailSection: React.FC<{
  title?: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  const classes = useStyles();

  return (
    <section className={classes.section}>
      {title ? <h3 className={classes.sectionTitle}>{title}</h3> : null}
      <div className={classes.grid}>{children}</div>
    </section>
  );
};

type ExtractionReviewCalloutProps = React.ComponentProps<
  typeof ExtractedResourceProvenance
>;

/**
 * Highlighted, top-of-modal panel surfacing the AI extraction review controls
 * (source document, approve/reject). Renders only for AI-extracted resources so
 * the review action is visible above the fold instead of buried beneath the
 * detail fields.
 */
export const ExtractionReviewCallout: React.FC<
  ExtractionReviewCalloutProps
> = props => {
  const classes = useStyles();

  if (!isDocumentExtractionV2Resource(props.resource)) {
    return null;
  }

  return (
    <div className={classes.callout} data-testid="extraction-review-callout">
      <ExtractedResourceProvenance {...props} />
    </div>
  );
};
